/**
 * Path routing on the History API, in about as little code as it takes.
 *
 * Every same origin link on the page is taken over here, so pages write plain
 * <a href="/settings"> and nothing else: no Link component to forget, and a link
 * still works when opened in a new tab. What is left to the browser: modified
 * clicks, other origins, downloads, targets, and a link to an anchor on the page
 * being shown. That last one is the prototype's bug run backwards. Under the
 * hash router an anchor was a route change; here a change of hash alone is not
 * a navigation at all, so the reader's anchors will simply scroll.
 */

import { useEffect, useLayoutEffect, useState } from "preact/hooks";

export interface Place {
  readonly pathname: string;
  readonly search: string;
}

type Scroll = { readonly to: "top" } | { readonly to: "saved"; readonly y: number } | { readonly to: "hash"; readonly id: string };

const listeners = new Set<() => void>();
let pendingScroll: Scroll | null = null;
let shown = "";

const keyOf = (place: Place) => `${place.pathname}${place.search}`;

function here(): Place {
  return { pathname: window.location.pathname, search: window.location.search };
}

function notify(): void {
  shown = keyOf(here());
  for (const listener of listeners) listener();
}

/** Remember where the page was scrolled, on the history entry being left. */
function keepScroll(): void {
  const state: unknown = window.history.state;
  const base = state && typeof state === "object" ? state : {};
  window.history.replaceState({ ...base, scrollY: window.scrollY }, "");
}

export function navigate(to: string, options: { readonly replace?: boolean } = {}): void {
  const url = new URL(to, window.location.href);
  if (url.origin !== window.location.origin) {
    window.location.assign(url.href);
    return;
  }
  const target = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  // The link to the page already showing: go to its top, but do not stack a
  // second copy of it in history for Back to walk through.
  if (options.replace || target === current) {
    window.history.replaceState(null, "", target);
  } else {
    keepScroll();
    window.history.pushState(null, "", target);
  }
  pendingScroll = url.hash ? { to: "hash", id: decodeURIComponent(url.hash.slice(1)) } : { to: "top" };
  notify();
}

function onClick(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest("a");
  if (!(anchor instanceof HTMLAnchorElement) || !anchor.hasAttribute("href")) return;
  if ((anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return;
  // An anchor on this very page: the browser scrolls to it, and no route changes.
  if (url.hash && keyOf(url) === keyOf(here())) return;

  event.preventDefault();
  navigate(`${url.pathname}${url.search}${url.hash}`);
}

function onPopState(event: PopStateEvent): void {
  // Back or forward across a hash change on one page is not a navigation.
  if (keyOf(here()) === shown) return;
  const state: unknown = event.state;
  const y = state && typeof state === "object" && "scrollY" in state ? Number(state.scrollY) : 0;
  pendingScroll = { to: "saved", y: Number.isFinite(y) ? y : 0 };
  notify();
}

let started = false;

/** Install the listeners, once. */
export function startRouter(): void {
  if (started) return;
  started = true;
  shown = keyOf(here());
  // The router puts the scroll back itself, after the page it belongs to has
  // rendered. The browser's own attempt lands on the page being left.
  if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  document.addEventListener("click", onClick);
  window.addEventListener("popstate", onPopState);
}

/** The current path and query, re-rendering on every navigation. */
export function usePlace(): Place {
  const [place, setPlace] = useState<Place>(here);
  useEffect(() => {
    const listener = () => setPlace(here());
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return place;
}

/**
 * Scroll where the last navigation asked, once the new page is in the DOM.
 * Call from the component that renders the page, keyed on the place.
 */
export function useScrollAfterNavigation(place: Place): void {
  useLayoutEffect(() => {
    const scroll = pendingScroll;
    pendingScroll = null;
    if (!scroll) return;
    if (scroll.to === "hash") {
      const element = document.getElementById(scroll.id);
      if (element) {
        element.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, scroll.to === "saved" ? scroll.y : 0);
  }, [place.pathname, place.search]);
}
